import type { PortalAccountSummary, PortalPermission } from '../types/portalUser.js';
import type { PortalNavSelection } from './portalReport/locationSelection.js';
import { PORTAL_DASHBOARD_PATH, PORTAL_LEADS_PATH, PORTAL_REPORTING_PATH, PORTAL_SETUP_PATH } from './kyckConnect/portalLinks.js';
import { PORTAL_OPERATIONAL_TYPES } from './portalScope.js';
import { STRATEGY_SECTIONS, type PortalStrategyNav } from './portalStrategyNav.js';

// CLIENT PORTAL — the left nav. Pure: the items, what is live, and what a
// location link points at.
//
// ⚠️⚠️ "CLUB ASSETS" IS CLUB VOCABULARY, AND THIS PRODUCT HAS TWO VERTICALS. A
// dealership is not a club, and the report spent a week acquiring
// `PortalReportLabels` so a brand can say "members" where another says "sales".
// The label here is BRAND-NEUTRAL — "Asset management" — rather than pulled from
// that registry (renamed from "Approved assets" 2026-10-13, Run F: the page also
// lists Not approved items, so the old name described a filter it never applied;
// "Asset management" keeps this brand-neutral rule), and the choice is deliberate:`PortalReportLabels` is the
// REPORT'S metric vocabulary (leads, sales, cost per lead), and stretching it to
// cover navigation would make one type answer two unrelated questions. A word
// that cannot be wrong for either vertical costs nothing and needs no registry
// entry nobody has asked to edit.
//
// ⚠️ THE COMING-SOON ITEMS ARE NOT LINKS. They carry no path at all, so there is
// nothing for a component to accidentally render as an anchor — the "genuinely
// non-interactive" requirement made structural rather than remembered. ⚠️ AND
// THEIR CHILDREN ARE THE SAME: `PortalNavChild` is its own type with no `path`
// field, so a sub-tree cannot contain a live destination either.

export type PortalNavItemId =
  | 'home'
  | 'reports'
  | 'strategy'
  | 'college'
  | 'inspiration'
  | 'kyckConnect'
  | 'assets'
  | 'campaigns'
  | 'organicSocial'
  | 'reviews'
  | 'bots'
  | 'services'
  | 'support';

/**
 * A child id (§6, 2026-09-03).
 *
 * ⚠️⚠️ NAMESPACED, BECAUSE "INSPIRATION" APPEARS TWICE — once under Campaigns
 * and once under Organic social. A bare `inspiration` would be two different
 * rows sharing one id, which is a duplicate React key and an ambiguous
 * reference the first time anything wants to point at one of them.
 */
export type PortalNavChildId =
  | 'strategy.monthly'
  | 'strategy.alternate'
  // ⚠️ Round 6 §3 — the ONE strategy child a non-fitness-franchise viewer gets.
  | 'strategy.campaigns'
  // ⚠️ Round 6 §4 — a REPORTS sub-row, not a strategy child. It is on this union
  // because a live child carries an id, and the nav's ids share one namespace.
  | 'reports.custom'
  | 'campaigns.liveAds'
  | 'campaigns.previews'
  // ⚠️ `campaigns.strategy` was DELETED (2026-09-23): a coming-soon "Strategy"
  // under Campaigns beside a LIVE "Strategy decks" tells a client we are building
  // a thing we already shipped.
  // ⚠️ The coming-soon Inspiration children under Campaigns / Organic social were REMOVED: Inspiration is one
  // top-level item, so exactly one Inspiration entry exists in the nav.
  | 'organicSocial.posting'
  | 'organicSocial.community'
  // ⚠️ Live only for a viewer holding `social` (KyckSocial). "Business profile", not
  // "Club profile": a dealership is not a club (see the header).
  | 'organicSocial.profile'
  // KyckConnect (Phase 2): the club's lead pages. Leads is the first live one; the Dashboard and Reporting come in Phase 3.
  | 'kyckConnect.dashboard'
  | 'kyckConnect.reporting'
  | 'kyckConnect.leads'
  // KyckConnect (Phase 4): the club's own Setup (its Speed to Lead call list moved here from the old "Who we call" page).
  | 'kyckConnect.setup'
  | 'organicSocial.analytics';

/**
 * A sub-item under a coming-soon section.
 *
 * ⚠️⚠️ ITS OWN TYPE WITH NO `path` FIELD, deliberately — not the item union with
 * an optional path. The whole reason the parents are genuinely non-interactive
 * is that a `status: 'soon'` item HAS nothing a component could render as an
 * anchor; a shared shape carrying `path?: string` would throw that away and put
 * the guarantee back on whoever writes the component.
 */
export interface PortalNavChild {
  id: PortalNavChildId;
  label: string;
}

/**
 * A sub-item under a LIVE section (2026-09-23, brief §4a).
 *
 * ⚠️⚠️ ITS OWN TYPE WITH A **REQUIRED** `path` — the mirror of `PortalNavChild`'s
 * absent one. The invariant is two facts held by two types, neither a
 * convention: a coming-soon child can NEVER be a link, and a live child ALWAYS
 * is. ⚠️ Do NOT "unify" the two by giving `PortalNavChild` an optional `path` —
 * that is the shared shape this file rejects, and it would let a coming-soon
 * child become a link by accident.
 */
export interface PortalNavLiveChild {
  id: PortalNavChildId;
  label: string;
  path: string;
}

export type PortalNavItem =
  /** Live: a real destination. ⚠️ `children?: never` — it has none. */
  | {
      id: PortalNavItemId;
      label: string;
      status: 'live';
      path: string;
      children?: never;
      /**
       * ⚠️ NAVIGATION, NOT A FEATURE (Run J — Home). The portal's intro page is a way
       * to get around, not something the portal contains, so it is excluded from
       * `liveNavItems` — the one list the home page's own "use the menu to move
       * around (…)" sentence is derived from. Read it through `navigationNavItems`.
       */
      navigationOnly?: true;
    }
  /**
   * Live SECTION HEADER with live children (2026-09-23) — "Strategy decks".
   *
   * ⚠️ `path?: never`: the header is a DISCLOSURE, not a destination, so
   * `isPortalNavItemActive` (which takes a path) cannot be called for it — its
   * children are what light up. `navSectionStartsOpen` returns true for every
   * live item, so it renders expanded with no change there.
   */
  | {
      id: PortalNavItemId;
      label: string;
      status: 'live';
      path?: never;
      children: readonly PortalNavLiveChild[];
    }
  /**
   * Coming soon: ⚠️ deliberately has NO `path` — see the header.
   *
   * ⚠️ `children` are coming soon too, by construction: a `PortalNavChild` has
   * no path either, so a sub-tree cannot contain a live destination.
   */
  | {
      id: PortalNavItemId;
      label: string;
      status: 'soon';
      children?: readonly PortalNavChild[];
    };

/**
 * The whole nav, in order.
 *
 * ⚠️ ONE muted heading over the group, rather than a badge per row. Scott's
 * brief: five "coming soon" pills shouting at once reads as an unfinished
 * product rather than a roadmap. Grouping them says the same thing once,
 * quietly, and the components below render no per-row marker at all.
 *
 * ⚠️⚠️ SENTENCE CASE (Scott, §6), with "AI" kept capitalised because it is an
 * initialism rather than a word. "AI chat bots", not "AI Chat Bots" and not
 * "Ai chat bots".
 *
 * ⚠️ THE ORDER IS THE ARCHITECT'S DEFAULT: the two sections with sub-trees sit
 * together in the middle, between the flat Asset management and the two
 * account-level items. Reversible.
 *
 * ⚠️ "Analytics" is Scott's own word for the Organic social child, and it sits
 * one level below a live "Reports" section that also shows analytics. Flagged
 * for the pre-client-launch copy pass rather than renamed here.
 */
/**
 * ⚠️⚠️ ROUND 6 — THE NAV IS NO LONGER THE SAME FOR EVERY CLIENT, and there is
 * deliberately NO exported static list any more. A default list would be one of
 * the two shapes, and anything that reached for it — the breadcrumb, the home
 * page's sentence — would show that shape to everyone: a dealership offered
 * "Monthly campaigns". Every caller goes through `portalNavFor(viewer)`.
 */
const STRATEGY_CHILD: Record<string, PortalNavLiveChild> = {
  'strategy.monthly': {
    id: 'strategy.monthly',
    label: 'Monthly campaigns',
    path: '/portal/strategy/monthly',
  },
  'strategy.alternate': {
    id: 'strategy.alternate',
    label: 'Alternate campaigns',
    path: '/portal/strategy/alternate',
  },
  'strategy.campaigns': {
    id: 'strategy.campaigns',
    label: 'Campaigns & promotions',
    path: '/portal/strategy/campaigns',
  },
};

/** The Custom reports sub-row under Reports (Round 6 §4). */
export const CUSTOM_REPORTS_CHILD: PortalNavLiveChild = {
  id: 'reports.custom',
  label: 'Custom reports',
  // ⚠️ `/portal/reports/…`, NOT `/portal/report/…`: under the report path it
  // would prefix-match Reports (`isPortalNavItemActive`), which is right for the
  // Meta and Google tabs and wrong for a page that is not the report.
  path: '/portal/reports/custom',
};

function navItems(
  strategyNav: PortalStrategyNav,
  hasSocial: boolean,
  hasInspiration: boolean,
  hasLeadConversations: boolean,
  hasWhoWeCall: boolean,
  hasKyckConnect: boolean,
  hasAiSms: boolean,
): readonly PortalNavItem[] {
  return [
  // ⚠️ HOME (Run J): once a client navigates away from the intro page there was no
  // way back. Navigation, not a feature — see `navigationOnly`.
  { id: 'home', label: 'Home', status: 'live', path: '/portal', navigationOnly: true },
  { id: 'reports', label: 'Reports', status: 'live', path: '/portal/report' },
  // ⚠️ THE LIBRARY (2026-09-23): top-level LIVE sections, not children of the
  // coming-soon Campaigns section (the architect's call, brief §0; reversible).
  // ⚠️ Round 6 §3: its CHILDREN are the viewer's — `STRATEGY_SECTIONS`.
  {
    id: 'strategy',
    label: 'Strategy decks',
    status: 'live',
    children: STRATEGY_SECTIONS[strategyNav].map((s) => {
      const child = STRATEGY_CHILD[s];
      if (!child) throw new Error(`No nav child for strategy section ${s}`);
      return child;
    }),
  },
  // ⚠️ SENTENCE CASE (rule 11) — "Kyck college", not "Kyck College". "Kyck" is
  // a proper noun, which is why `liveSectionsPhrase` must not lowercase it.
  { id: 'college', label: 'Kyck college', status: 'live', path: '/portal/college' },
  // ⚠️ INSPIRATION (Run I) — shown ONLY when the viewer has at least one published entry in their
  // audience (the Custom reports rule: an empty catch-all invites "where is my inspiration?"). The
  // decision is the server's, via the same list endpoint the page reads.
  ...(hasInspiration
    ? [{ id: 'inspiration' as const, label: 'Inspiration', status: 'live' as const, path: '/portal/inspiration' }]
    : []),
  // ⚠️ KYCKCONNECT (Phase 2, Setup added in Phase 4) - a live group, shown ONLY when the viewer's scope has a lead bot or a club with
  // Speed to Lead set up (the Inspiration rule: an empty catch-all invites "where are my leads?"). It REPLACES the old "Lead
  // conversations" and "Who we call" items, whose addresses now redirect (to Leads and to Setup). No permission to SEE it: access is
  // "every portal user whose scope includes the club", decided server-side; editing Setup needs `kyckconnect_setup`, also
  // server-side. The Dashboard and Reporting join this group in Phase 3.
  ...(hasLeadConversations || hasWhoWeCall || hasKyckConnect
    ? [{
        id: 'kyckConnect' as const,
        label: 'KyckConnect',
        status: 'live' as const,
        children: [
          { id: 'kyckConnect.dashboard' as const, label: 'Dashboard', path: PORTAL_DASHBOARD_PATH },
          { id: 'kyckConnect.leads' as const, label: 'Leads', path: PORTAL_LEADS_PATH },
          { id: 'kyckConnect.reporting' as const, label: 'Reporting', path: PORTAL_REPORTING_PATH },
          { id: 'kyckConnect.setup' as const, label: 'Setup', path: PORTAL_SETUP_PATH },
        ],
      }]
    : []),
  // ⚠️⚠️ LIVE FOR EVERY PORTAL USER, NOT CONDITIONALLY ON THE `assets` PERMISSION
  // (Scott, 2026-10-09). A conditional nav is a structural change he may want to
  // reverse, and people asking for access is a demand signal worth having. The
  // PERMISSION is still the gate — every route refuses a user without it — and
  // the page tells them it is not switched on (assetsNotSwitchedOn).
  { id: 'assets', label: 'Asset management', status: 'live', path: '/portal/assets' },
  {
    id: 'campaigns',
    label: 'Campaigns',
    status: 'soon',
    children: [
      { id: 'campaigns.liveAds', label: 'Live ads' },
      { id: 'campaigns.previews', label: 'Previews' },
    ],
  },
  // ⚠️⚠️ KYCKSOCIAL LIVES UNDER ORGANIC SOCIAL (2026-10-13, Scott: the tabs
  // "deserve their own place in the menu"). For a viewer holding the `social`
  // permission this section is a LIVE header with three live children; for
  // everyone else it is the coming-soon section it always was, four children and
  // no paths. ⚠️ THE COST, DELIBERATE: a pilot club loses the coming-soon
  // Analytics and Inspiration rows, because PortalNavItem has no variant mixing
  // live and coming-soon children. That union is on the deferral register.
  hasSocial
    ? {
        id: 'organicSocial',
        label: 'Organic social',
        status: 'live',
        children: [
          { id: 'organicSocial.posting', label: 'Posting', path: '/portal/social/posts' },
          { id: 'organicSocial.community', label: 'Community management', path: '/portal/social/inbox' },
          { id: 'organicSocial.profile', label: 'Business profile', path: '/portal/social/profile' },
        ],
      }
    : {
        id: 'organicSocial',
        label: 'Organic social',
        status: 'soon',
        children: [
          { id: 'organicSocial.posting', label: 'Posting' },
          { id: 'organicSocial.community', label: 'Community management' },
          { id: 'organicSocial.analytics', label: 'Analytics' },
        ],
      },
  // ⚠️⚠️ A PROMISE THE NAV DOES NOT ECHO READS AS SOMETHING WE FORGOT TO BUILD
  // (round 9). The home page's welcome copy names what the portal will hold, and
  // review management is part of what kyck does for these clients — so it belongs
  // in the same coming-soon list rather than only in prose.
  //
  // ⚠️ SENTENCE CASE, like every sibling. "AI" stays capitalised because it is an
  // initialism, which is the one exception already in this list.
  //
  // ⚠️ ITS OWN TOP-LEVEL ITEM rather than a child of Organic social. Reviews arrive
  // on Google Business as much as on Facebook, and nesting them under "Organic
  // social" would tell a dealership their Google reviews are a social-media
  // feature. `organicSocial.community` is the INBOX; this is the reputation side.
  { id: 'reviews', label: 'Review management', status: 'soon' },
  // ⚠️ "AI chat bots" is a PROMISE ("coming soon"); a viewer whose clubs already run AI SMS has the thing, in KyckConnect, so the promise would
  // read as a feature we forgot we shipped. It is hidden for them and stays for everyone else.
  ...(hasAiSms ? [] : [{ id: 'bots' as const, label: 'AI chat bots', status: 'soon' as const }]),
  { id: 'services', label: 'Services', status: 'soon' },
  { id: 'support', label: 'Support', status: 'soon' },
  ];
}

// ---------------------------------------------------------------------------
// ⚠️⚠️ ROUND 6 — THE NAV REFLECTS WHAT THIS CLIENT ACTUALLY HAS.
//
// ONE idea with TWO inputs, written here and nowhere else:
//   - INDUSTRY decides the strategy children (`strategyNav`, resolved on the
//     SERVER from the scope and sent as ONE value — never per-account
//     industries branched on in the browser);
//   - CONTENT decides whether Custom reports appears (`hasCustomReports`, from
//     the viewer's own visible tiles in that section).
//
// ⚠️ Custom reports is shown ONLY when there is one. Unlike Alternate campaigns
// — whose empty page is a sentence about what the section is — an empty
// "Custom reports" is a catch-all with no natural content, and it invites
// "where are my custom reports?".
// ---------------------------------------------------------------------------

export type PortalNavViewer = {
  strategyNav: PortalStrategyNav;
  hasCustomReports: boolean;
  /** Holds the `social` permission. Optional so every existing caller is unchanged. */
  hasSocial?: boolean;
  /** Has at least one published Inspiration entry in their audience. Optional so every existing caller is unchanged. */
  hasInspiration?: boolean;
  /** The viewer's scope has at least one lead-conversations bot. Optional so every existing caller is unchanged. */
  hasLeadConversations?: boolean;
  /** At least one of the viewer's clubs has Speed to Lead set up. Optional so every existing caller is unchanged. */
  hasWhoWeCall?: boolean;
  /** At least one of the viewer's clubs has any KyckConnect service (the lead relay included, so a relay-only club gets the group). */
  hasKyckConnect?: boolean;
  /** At least one of the viewer's clubs has AI SMS running (a switched-on service with an active bot). Hides the coming-soon "AI chat bots" row. */
  hasAiSms?: boolean;
};

export type PortalViewerNav = {
  items: readonly PortalNavItem[];
  /** The Custom reports row under Reports, or null when the viewer has none. */
  customReports: PortalNavLiveChild | null;
};

export function portalNavFor(viewer: PortalNavViewer): PortalViewerNav {
  return {
    items: navItems(
      viewer.strategyNav,
      viewer.hasSocial === true,
      viewer.hasInspiration === true,
      viewer.hasLeadConversations === true,
      viewer.hasWhoWeCall === true,
      viewer.hasKyckConnect === true,
      viewer.hasAiSms === true,
    ),
    customReports: viewer.hasCustomReports ? CUSTOM_REPORTS_CHILD : null,
  };
}

export const PORTAL_NAV_SOON_HEADING = 'Coming soon';

/**
 * Does this section start EXPANDED (§3, 2026-09-04)?
 *
 * Scott: *"can reports be the only one that is [un]truncated on open. All others
 * as is."* So: **a LIVE section is always open, a coming-soon section is always
 * collapsed** — which today means Reports and nothing else.
 *
 * ⚠️⚠️ THE OLD RULE IS GONE, NOT LAYERED UNDER THIS ONE. Round 10 §5 opened
 * *the section containing the active item*, which left Reports shut on the home
 * page. Keeping both would be two mechanisms deciding one disclosure, and a
 * section that is open on one route and shut on another for no visible reason is
 * what that produces.
 *
 * ⚠️⚠️ AND THE PATHNAME PARAMETER WENT WITH IT, WHICH IS WHAT MAKES THAT
 * STRUCTURAL. With nothing to compare a route against, the retired rule cannot
 * be half-reintroduced here; it would have to be built again on purpose.
 *
 * ⚠️⚠️ IT IS ALSO STRICTLY SAFER, AND IT RETIRES A STANDING WORRY. The club
 * sublist is the ONLY route into a single club's report — the restyled tables
 * have no drill-down — so a rule that SOMETIMES collapsed it sometimes hid a
 * client's only navigation. *Always expanded* cannot.
 *
 * ⚠️ THE ACTIVE-ITEM UNDERLINE IS UNTOUCHED. That is what says which club is
 * showing, and it was never the thing this function decided.
 */
export function navSectionStartsOpen(item: PortalNavItem): boolean {
  return item.status === 'live';
}

/**
 * What the portal currently holds, named from the LIVE nav items (§4, 2026-09-04).
 *
 * ⚠️⚠️ DERIVED, NEVER TYPED BY HAND. Scott's line is "use the menu to navigate to
 * the portal features (just reports right now)" — and "just reports right now" is
 * true until the second live item ships, at which point it is WRONG on a
 * client-facing page. A hand-typed count is a promise to remember something
 * nobody will; this updates itself the day Asset management goes live.
 *
 * ⚠️ LOWERCASED, because it lands mid-sentence and rule 11 is sentence case. The
 * nav's own labels are title-ish ("Asset management"), so they cannot be dropped in
 * verbatim.
 *
 * ⚠️ "just X right now" ONLY READS WELL FOR ONE. Past that the word "just" is
 * doing no work and starts to sound apologetic about a growing product, so the
 * phrasing changes with the count rather than the list simply getting longer
 * inside a sentence built for one item.
 */
export function liveSectionsPhrase(items: readonly PortalNavItem[]): string {
  const names = liveNavItems(items).map((i) => midSentenceLabel(i.label));
  // ⚠️ An empty live nav is not reachable today (Reports is live) but is a real
  // state during a rollback, and "just  right now" would be the visible result.
  if (names.length === 0) return 'nothing live just yet';
  if (names.length === 1) return `just ${names[0]} right now`;
  const last = names[names.length - 1] as string;
  return `${names.slice(0, -1).join(', ')} and ${last} right now`;
}

/**
 * A sentence-cased nav label, lowered for the middle of a sentence.
 *
 * ⚠️⚠️ ONLY THE FIRST WORD CAN BE CAPITALISED (the labels are sentence case),
 * and it is left alone when it is a PROPER NOUN or an INITIALISM: "Kyck
 * college" must stay "Kyck college" — "kyck college" names our own company in
 * lower case on a client's home page — and "AI chat bots" must keep "AI". The
 * 2026-09-23 library is the first time the many-items branch runs for real, and
 * the first live label to start with a proper noun.
 */
const MID_SENTENCE_KEEP = new Set(['Kyck']);

export function midSentenceLabel(label: string): string {
  const [first = '', ...rest] = label.split(' ');
  const keep = MID_SENTENCE_KEEP.has(first) || /^[A-Z]{2,}$/.test(first);
  return [keep ? first : first.toLowerCase(), ...rest].join(' ');
}

/** A live item that is a destination, as opposed to a section header. */
export function isLiveLink(
  item: PortalNavItem,
): item is Extract<PortalNavItem, { status: 'live'; path: string }> {
  return item.status === 'live' && typeof item.path === 'string';
}

/** A live SECTION HEADER — no destination of its own, only live children. */
export function isLiveHeader(
  item: PortalNavItem,
): item is Extract<PortalNavItem, { status: 'live'; children: readonly PortalNavLiveChild[] }> {
  return item.status === 'live' && item.path === undefined;
}

/**
 * The live FEATURES, in order.
 *
 * ⚠️⚠️ EXCLUDES `navigationOnly` ROWS (Home). This is the list `liveSectionsPhrase`
 * names the portal's contents from — deliberately one fact with the nav — and a
 * client must not be told the portal contains "home". Excluding it HERE, rather
 * than in the phrase, keeps every other reader of "what is live" honest too.
 */
export function liveNavItems(
  items: readonly PortalNavItem[],
): Array<Extract<PortalNavItem, { status: 'live' }>> {
  return items.filter(
    (i): i is Extract<PortalNavItem, { status: 'live' }> =>
      i.status === 'live' && !(i.path !== undefined && i.navigationOnly === true),
  );
}

/** Rows that help a client get around but are not features (Home), in order. */
export function navigationNavItems(
  items: readonly PortalNavItem[],
): Array<Extract<PortalNavItem, { status: 'live'; path: string }>> {
  return items.filter(
    (i): i is Extract<PortalNavItem, { status: 'live'; path: string }> =>
      i.status === 'live' && i.path !== undefined && i.navigationOnly === true,
  );
}

/**
 * Is Home the page being viewed? ⚠️ EXACT, never a prefix: every portal route
 * starts with `/portal`, so a prefix match would light Home everywhere.
 */
export function isPortalHomeActive(pathname: string): boolean {
  return pathname === '/portal' || pathname === '/portal/';
}

export function soonNavItems(
  items: readonly PortalNavItem[],
): Array<Extract<PortalNavItem, { status: 'soon' }>> {
  return items.filter(
    (i): i is Extract<PortalNavItem, { status: 'soon' }> => i.status === 'soon',
  );
}

/**
 * Whether a live item is the one being viewed.
 *
 * Prefix-matched, so `/portal/report/meta` still highlights Reports — a client
 * on the Meta tab has not left the section.
 */
export function isPortalNavItemActive(pathname: string, itemPath: string): boolean {
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

/**
 * Whether a CLUB ROW is the one being viewed (§B, 2026-09-03).
 *
 * ⚠️⚠️ IT TAKES THE RESOLVED SELECTION, NOT A PATHNAME. Which club is showing
 * is decided by a QUERY PARAMETER, and every club row shares one path — so
 * `NavLink`'s own `isActive` would light all of them at once. That half of the
 * old reasoning was right; what was wrong was the compensating control it named
 * ("the report's own header names the club") — the header printed "All 6
 * locations" over one club, so there was no active state anywhere.
 *
 * ⚠️ A `subset` selection matches NO ROW. Lighting "All locations" for a
 * two-of-three selection would state something untrue, and no single club row is
 * right either — so the honest rendering of "several" is nothing highlighted.
 *
 * ⚠️ NO SELECTION AT ALL matches no row either: the nav renders on pages with no
 * report (the home page), where no club is being shown.
 */
export function isPortalClubRowActive(
  selection: PortalNavSelection | null | undefined,
  /** The row's own account id — `null` is the "All locations" row. */
  rowAccountId: string | null,
): boolean {
  if (!selection) return false;
  if (selection.kind === 'all') return rowAccountId === null;
  if (selection.kind === 'account') return rowAccountId === selection.accountId;
  return false;
}

// ---------------------------------------------------------------------------
// ⚠️⚠️ ROUND 6 §4 — A THIRD KIND OF ROW IN A LIST OF TWO.
//
// The Reports sub-list was "All locations" plus one row per account, and
// `isPortalClubRowActive` reads `rowAccountId: string | null` as a BINARY — null
// is "All locations". Custom reports is NEITHER, so it gets its OWN row kind
// rather than riding in as an account with a fake id: a fake id looks fine
// until a client's real account id collides with it, and `null` would light
// "All locations". The active state is decided by kind, below.
// ---------------------------------------------------------------------------

export type PortalReportRow =
  | { kind: 'location'; link: PortalReportLink }
  | { kind: 'custom'; child: PortalNavLiveChild };

/** The Reports sub-list: the location rows, then Custom reports when the viewer has one. */
export function portalReportRows(
  locations: readonly PortalReportLink[],
  nav: PortalViewerNav,
): PortalReportRow[] {
  const rows: PortalReportRow[] = locations.map((link) => ({ kind: 'location', link }));
  if (nav.customReports) rows.push({ kind: 'custom', child: nav.customReports });
  return rows;
}

/** A stable React key per row — never an account id doubling for something else. */
export function portalReportRowKey(row: PortalReportRow): string {
  return row.kind === 'custom' ? row.child.id : `loc:${row.link.accountId ?? 'all'}`;
}

/**
 * Whether a Reports sub-row is the one being viewed.
 *
 * ⚠️ ON THE CUSTOM REPORTS PAGE NO LOCATION ROW IS ACTIVE — "All locations"
 * included — whatever selection is handed in. That page is not a report, so no
 * club is being shown; the guard is explicit rather than trusting that the page
 * happens to pass no selection.
 */
export function isPortalReportRowActive(
  row: PortalReportRow,
  selection: PortalNavSelection | null | undefined,
  pathname: string,
): boolean {
  const onCustom = isPortalNavItemActive(pathname, CUSTOM_REPORTS_CHILD.path);
  if (row.kind === 'custom') return onCustom;
  if (onCustom) return false;
  return isPortalClubRowActive(selection, row.link.accountId);
}

// ---------------------------------------------------------------------------
// The report's own sub-links: one per location the scope resolves to.
// ---------------------------------------------------------------------------

export type PortalReportLink = {
  accountId: string | null;
  name: string;
  href: string;
};

export const ALL_LOCATIONS_LABEL = 'All locations';

/**
 * Where a report for `accountId` lives.
 *
 * ⚠️⚠️ THE SELECTED ACCOUNT RIDES THE URL, WHICH IS WHAT MAKES THE NAV POSSIBLE
 * AT ALL — it used to be component state, so there was nothing to link to.
 *
 * ⚠️⚠️ AND `null` IS A REAL DESTINATION, NOT AN ABSENCE. `/portal/report` with no
 * account is the WHOLE SCOPE: for a single-location client that is their club,
 * and for a GROUP client it is the group-level view. The group report template
 * is explicitly out of scope for this build — but the routing has to leave room
 * for it, because the alternative is dropping a group-scoped client into one
 * club's report and calling it their report. That is the failure this shape
 * prevents, so do not "simplify" it by defaulting the link to the first account.
 */
export function portalReportHref(accountId: string | null, tab?: string): string {
  const base = tab && tab !== 'overview' ? `/portal/report/${tab}` : '/portal/report';
  return accountId ? `${base}?account=${encodeURIComponent(accountId)}` : base;
}

/**
 * The report links a client's scope resolves to: "All locations" first, then one
 * per operational account.
 *
 * ⚠️ ONLY OPERATIONAL accounts get a row. An intermediate GROUP is selectable
 * (it narrows to its own locations) but is structural plumbing rather than
 * somewhere a client thinks of going — the same distinction `PortalScope` draws
 * between `selectableAccountIds` and `accounts`.
 *
 * ⚠️ "All locations" is OMITTED when there is exactly one, because "All
 * locations" above a list of one is noise, and clicking it would go to the same
 * numbers under a different name.
 */
export function portalReportLinks(
  accounts: readonly PortalAccountSummary[],
  tab?: string,
): PortalReportLink[] {
  const locations = accounts
    .filter((a) => PORTAL_OPERATIONAL_TYPES.includes(a.accountType))
    .map((a) => ({ accountId: a.id, name: a.name, href: portalReportHref(a.id, tab) }));
  if (locations.length <= 1) return locations;
  return [
    { accountId: null, name: ALL_LOCATIONS_LABEL, href: portalReportHref(null, tab) },
    ...locations,
  ];
}

/** Can this client open the report at all? */
export function canSeeReports(permissions: readonly PortalPermission[]): boolean {
  return permissions.includes('reporting');
}
